/** @jsx jsx */
import { jsx } from 'theme-ui'
import * as React from 'react'
import { Global } from '@emotion/react'
import SEO from './seo'
import Head from '@/components/Home/Head'
import LpHeader from '@/components/Home/LpHeader'
import { FooterList } from './FooterList'
import Footer from './footer'
import '../styles/global.css'
import Breadcrumbs, { BreadcrumbItem } from '@/components/Common/Breadcrumbs'
import ContactPopup from '@/components/Home/ContactPopup'
import { blogCategories } from '@/data/blogCategories'

type LayoutProps = {
  children: React.ReactNode
  className?: string
  location?: Pick<Location, 'pathname'>
}
const Layout = ({ children, className = ``, location }: LayoutProps) => {
  const pathname = location?.pathname || '/'
  const normalizedPath = pathname.replace(/\/$/, '') || '/'
  const isProjectListRoute = normalizedPath.startsWith('/project-list')
  const isBlogDetailsDetailRoute = normalizedPath.startsWith('/blog-details/')
  const isInterviewUserDetailRoute =
    normalizedPath.startsWith('/interview-user/')
  const isInterviewCompanyDetailRoute = normalizedPath.startsWith(
    '/interview-company/',
  )
  const isGlossaryDetailRoute = normalizedPath.startsWith('/glossary/')
  const isExcludedContactPopup =
    normalizedPath === '/terms' || normalizedPath === '/privacy'
  const isFavoritesRoute = normalizedPath.startsWith('/favorites')
  const isSkillProjectListRoute = normalizedPath.startsWith('/project/skills/')
  const isProjectTypeProjectListRoute =
    normalizedPath.startsWith('/project/types/')
  const isWorkingProjectListRoute = normalizedPath.startsWith('/project/works/')
  const isConditionProjectListRoute = normalizedPath.startsWith(
    '/project/conditions/',
  )
  const isLpRoute = normalizedPath.startsWith('/lp')
  const isForCompaniesRoute = normalizedPath === '/for-companies'
  const isMatchingRoute = normalizedPath === '/matching'

  const labelMap: Record<string, string> = {
    blog: 'ブログ',
    category: 'カテゴリー',
    'blog-details': 'ブログ',
    'project-list': '案件一覧',
    news: 'ニュース',
    faq: 'FAQ',
    'interview-company': '企業インタビュー',
    'interview-user': 'エンジニアインタビュー',
    privacy: 'プライバシー',
    terms: '利用規約',
    'value-check': 'エンジニア市場価値診断',
    coordinator: 'コーディネーター紹介',
    'freelance-start': 'フリーランス入門ガイド',
    consultation: '無料個別カウンセリング',
    'contact-engineer': 'エンジニアお問い合わせ',
    'contact-company': '企業様お問い合わせ',
    contact: 'お問い合わせ（総合）',
    'security-compliance': 'セキュリティ・コンプライアンス',
    partners: 'パートナー企業・協業のご案内',
    features: 'エンジビズの特徴',
    press: 'メディア掲載実績・プレスリリース',
    'skill-sheet': 'スキルシート対策',
    glossary: 'IT用語集',
    diagnosis: 'フリーランスエンジニアの単価診断',
    tools: 'ツール',
    'tax-simulator': 'フリーランス手取りシミュレーター',
    'settlement-calculator': '精算幅計算ツール',
    matching: '人材見積もり診断',
    schedule: '相談予約',
    ...Object.fromEntries(
      blogCategories.map(({ slug, cmsLabel }) => [slug, cmsLabel]),
    ),
  }

  const buildItems = (path: string): BreadcrumbItem[] => {
    if (path === '/') return []
    const blogCategoryMatch = path.match(
      /^\/blog\/category\/([^/]+)(?:\/page\/(\d+))?$/,
    )
    if (blogCategoryMatch) {
      const [, categorySlug, pageNumber] = blogCategoryMatch
      const categoryPath = `/blog/category/${categorySlug}/`
      const items: BreadcrumbItem[] = [
        { name: 'ホーム', url: '/' },
        { name: 'ブログ', url: '/blog-details/' },
        {
          name: labelMap[categorySlug] || decodeURIComponent(categorySlug),
          url: categoryPath,
        },
      ]
      if (pageNumber) {
        items.push({
          name: `${pageNumber}ページ目`,
          url: `${categoryPath}page/${pageNumber}/`,
        })
      }
      return items
    }
    const segments = path.split('/').filter(Boolean)
    const items: BreadcrumbItem[] = [{ name: 'ホーム', url: '/' }]
    let current = ''
    segments.forEach((seg, index) => {
      current += `/${seg}`
      if (seg === 'page') return
      const isLast = index === segments.length - 1
      const name =
        segments[index - 1] === 'page'
          ? `${decodeURIComponent(seg)}ページ目`
          : labelMap[seg] || decodeURIComponent(seg)
      const url = current === '/' ? '/' : `${current}/`
      items.push({ name: isLast ? name : name, url })
    })
    return items
  }

  const breadcrumbItems = !(
    isProjectListRoute ||
    isBlogDetailsDetailRoute ||
    isInterviewUserDetailRoute ||
    isInterviewCompanyDetailRoute ||
    isGlossaryDetailRoute ||
    isFavoritesRoute ||
    isSkillProjectListRoute ||
    isProjectTypeProjectListRoute ||
    isWorkingProjectListRoute ||
    isConditionProjectListRoute ||
    isLpRoute ||
    isForCompaniesRoute ||
    isMatchingRoute
  )
    ? buildItems(normalizedPath)
    : []

  return (
    <React.Fragment>
      <a href="#main-content" className="skip-link">
        メインコンテンツへスキップ
      </a>
      <Global
        styles={(theme) => ({
          '*': {
            boxSizing: `inherit`,
            '&:before': {
              boxSizing: `inherit`,
            },
            '&:after': {
              boxSizing: `inherit`,
            },
          },
          html: {
            fontSize: `18px`,
            WebkitTextSizeAdjust: `100%`,
          },
          img: {
            borderStyle: `none`,
          },
          pre: {
            fontFamily: `monospace`,
            fontSize: `1em`,
          },
          '[hidden]': {
            display: `none`,
          },
          'body.not-found-page .layout-breadcrumbs': {
            display: 'none',
          },
          // '::selection': {
          //   backgroundColor: theme.colors.primary,
          //   color: theme.colors.background,
          // },
        })}
      />
      <SEO />
      {!isLpRoute ? (
        <header role="banner">
          <Head pathname={pathname} />
        </header>
      ) : (
        <LpHeader />
      )}
      <main
        id="main-content"
        className={className}
        sx={{
          margin: '0 auto',
          maxWidth: '1200px',
          padding: '0',
          marginTop: ['60px', '80px'],
        }}
      >
        {breadcrumbItems.length > 0 && (
          <div className="layout-breadcrumbs">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        )}
        {children}
      </main>
      <FooterList />
      <Footer />
      {!isExcludedContactPopup && <ContactPopup />}
    </React.Fragment>
  )
}

export default Layout
